/**
 * UnifiedBookingService — single source of truth for marketplace booking, shared by BOTH the
 * Contractor app and the Customer Portal (imported from @tradesmatepro/shared-ui). It was
 * previously duplicated per app and the two copies drifted; this merged version is the union of
 * both. `requesterType` ('customer' | 'contractor') distinguishes the two flows; the
 * marketplace-standalone API remains the source of truth for the actual create.
 *
 * Pass supabase + requesterType via the factory: createUnifiedBookingService(supabase, 'contractor').
 */

import { normalizeStateToCode } from '../utils/usStates';

// Dynamic marketplace API base URL based on current domain
function getMarketplaceApiBase() {
  if (typeof window !== 'undefined') {
    const currentHostname = window.location.hostname;
    if (currentHostname.includes('smbmate')) {
      return 'https://marketplace.smbmate.com';
    }
    return 'https://marketplace.tradesmatepro.com';
  }
  // Fallback for server-side or when window is not available
  return (typeof process !== 'undefined' && process.env && process.env.REACT_APP_MARKETPLACE_API_URL)
    || 'https://marketplace.tradesmatepro.com';
}

const MARKETPLACE_API_BASE = getMarketplaceApiBase();

export class UnifiedBookingService {
  constructor(supabaseClient, requesterType = 'customer') {
    this.supabase = supabaseClient;
    this.requesterType = requesterType; // 'customer' | 'contractor'
  }

  /**
   * Create a marketplace request via the unified API (customer OR contractor).
   */
  async createRequest(requestData, tags = []) {
    try {
      // Get the current session (refresh if it's about to expire)
      let { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Authentication required - please log in again');
      }
      const now = Math.floor(Date.now() / 1000);
      if (session.expires_at && now >= session.expires_at - 60) {
        const { data: refreshData, error: refreshError } = await this.supabase.auth.refreshSession();
        if (refreshError || !refreshData.session) {
          throw new Error('Session expired - please log in again');
        }
        session = refreshData.session;
      }

      // Derive pricing_preference from pricing data if not explicitly set
      let pricingPreference = requestData.pricing_preference;
      if (!pricingPreference) {
        if (requestData.flat_rate) pricingPreference = 'FLAT';
        else if (requestData.hourly_rate) pricingPreference = 'HOURLY';
        else if (requestData.budget_min || requestData.budget_max) pricingPreference = 'RANGE';
        else pricingPreference = 'NEGOTIABLE';
      }

      const payload = {
        // Identity: requesterId MUST equal auth.uid() (the API 403s otherwise), so take it from
        // the authenticated session — correct for BOTH customer and contractor flows.
        requesterType: this.requesterType,
        requesterId: session.user.id,
        customer_id: requestData.customer_id ?? null,

        // Core fields
        title: requestData.title,
        description: requestData.description,

        // Classification
        request_type: requestData.request_type || (requestData.is_emergency ? 'EMERGENCY' : 'STANDARD'),
        service_mode: requestData.service_mode || 'ONSITE',
        pricing_preference: pricingPreference,

        // Budget / pricing
        budget_min: requestData.budget_min,
        budget_max: requestData.budget_max,
        flat_rate: requestData.flat_rate,
        hourly_rate: requestData.hourly_rate,

        // Location — accept the BookingForm shape OR the legacy service_* shape
        location_address: requestData.location_address ?? requestData.service_address,
        location_city: requestData.location_city ?? requestData.service_city,
        // Normalised, not trusted. The pickers upstream now emit 2-letter codes, but this
        // service is the ONE chokepoint every request-create in both apps passes through,
        // and marketplace_requests.service_state is written straight from this value
        // (marketplace-standalone pages/api/create-request.js). Live rows already carry
        // lowercase "or" from before the picker existed. normalizeStateToCode passes
        // unrecognised/international values through unchanged, so this cannot damage a
        // legitimately non-US state.
        location_state: normalizeStateToCode(requestData.location_state ?? requestData.service_state) || null,
        location_postal_code: requestData.location_postal_code ?? requestData.service_zip,

        // Timing — accept either shape
        start_time: requestData.start_time ?? requestData.preferred_time_start,
        end_time: requestData.end_time ?? requestData.preferred_time_end,
        preferred_time_option: requestData.preferred_time_option || 'flexible',

        // Response control
        max_responses: requestData.max_responses ?? requestData.response_control,
        unlimited_responses: requestData.unlimited_responses,

        // Options / quality signals (contractor-specific fields are harmless for customer)
        requires_inspection: requestData.requires_inspection,
        is_emergency: requestData.is_emergency || requestData.request_type === 'EMERGENCY',
        seriousness_level: requestData.seriousness_level,
        is_flexible: requestData.is_flexible,
        allow_alternative_dates: requestData.allow_alternative_dates,

        // Photos + tags
        photo_urls: requestData.photo_urls || [],
        tags
      };

      const response = await fetch(`${MARKETPLACE_API_BASE}/api/create-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) throw new Error('Authentication failed - please log in again');
        if (response.status === 403) throw new Error('Access denied - insufficient permissions');
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.request;
    } catch (error) {
      console.error('❌ UnifiedBookingService.createRequest error:', error);
      throw error;
    }
  }

  /**
   * Get a customer's marketplace requests (matches legacy customer_id OR the marketplace
   * customer_user_id identity).
   */
  async getCustomerRequests(customerId, authUserId) {
    try {
      let query = this.supabase
        .from('marketplace_requests')
        .select(`
          *,
          request_tags (
            tags (id, name, category)
          )
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (authUserId && customerId) {
        query = query.or(`customer_id.eq.${customerId},customer_user_id.eq.${authUserId}`);
      } else if (authUserId) {
        query = query.eq('customer_user_id', authUserId);
      } else if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('UnifiedBookingService.getCustomerRequests error:', error);
      throw error;
    }
  }

  /**
   * Get a company's marketplace requests (contractor-as-requester).
   */
  async getCompanyRequests(companyId) {
    try {
      const { data, error } = await this.supabase
        .from('marketplace_requests')
        .select(`
          *,
          request_tags (
            tags (id, name, category)
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('UnifiedBookingService.getCompanyRequests error:', error);
      throw error;
    }
  }

  /**
   * Get marketplace responses for a request.
   */
  async getRequestResponses(requestId) {
    try {
      const { data, error } = await this.supabase
        .from('marketplace_responses')
        .select(`
          *,
          companies!marketplace_responses_contractor_company_id_fkey (
            id,
            name,
            email,
            phone,
            avg_rating,
            rating_count,
            company_logo_url
          )
        `)
        .eq('request_id', requestId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('UnifiedBookingService.getRequestResponses error:', error);
      throw error;
    }
  }

  /**
   * Express interest in a marketplace request (contractor responding to a request).
   */
  async expressInterest(requestId, companyId, message = null) {
    try {
      const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Authentication required');

      const response = await fetch(`${MARKETPLACE_API_BASE}/api/express-interest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ job_id: requestId, company_id: companyId, message })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('❌ UnifiedBookingService.expressInterest error:', error);
      throw error;
    }
  }

  /**
   * Re-open an expired marketplace request (preserves photos, description, analytics).
   */
  async reopenRequest(requestId) {
    try {
      const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Authentication required');

      const response = await fetch(`${MARKETPLACE_API_BASE}/api/reopen-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ request_id: requestId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('❌ UnifiedBookingService.reopenRequest error:', error);
      throw error;
    }
  }
}

// Factory. requesterType defaults to 'customer'; the contractor app passes 'contractor'.
export function createUnifiedBookingService(supabaseClient, requesterType = 'customer') {
  return new UnifiedBookingService(supabaseClient, requesterType);
}

export default UnifiedBookingService;
